import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
    Box,
    Divider,
    IconButton,
    Paper,
    Popover,
    Stack,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import {
    HelpOutline,
    KeyboardArrowDown,
    KeyboardArrowLeft,
    KeyboardArrowRight,
    KeyboardArrowUp,
    OpenWith,
    Redo,
    Undo,
    ZoomIn,
    ZoomOut,
} from '@mui/icons-material';
import {Kbd} from './Kbd';
import {useViewportControls} from '../hooks/useViewportControls';
import {IS_MAC} from "../../../../../utils/is-mac";
import {PAN_STEP, ZOOM_MAX, ZOOM_MIN} from "../utils/constants";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import SettingsIcon from "@mui/icons-material/Settings";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import {useNavigate, useSearchParams} from "react-router-dom";
import {
    useCurrentApp,
    useCurrentAppId
} from "crqlar-react-common/dist/utils/hooks/app/useOrganizationApps";
import {useSector} from "crqlar-react-common/dist/utils/hooks/sector/useSectors";
import {useFloorplanContext} from "../hooks/useFloorplanContext";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";

interface BottomMenuProps {
    onOpenDrawer?: () => void;
    onHideReservations?: () => void;
    onUndo?: () => void;
    onRedo?: () => void;
    canUndo?: boolean;
    canRedo?: boolean;
}

const flatMenuGroupSx = {
    overflow: 'hidden',
    bgcolor: 'background.paper',
    borderColor: 'divider',
    borderRadius: 2,
    pointerEvents: 'auto',
} as const;

const BottomMenu: React.FC<BottomMenuProps> = ({
    onOpenDrawer,
    onHideReservations,
    onUndo,
    onRedo,
    canUndo,
    canRedo,
}) => {
    const {t} = useTranslation();

    const readOnlyUser = useReadOnly()

    const {
        zoom,
        zoomIn,
        zoomOut,
        pan,
        fitToView,
        gridStyle,
        isEditMode
    } = useViewportControls();
    const {restaurantId} = useFloorplanContext();
    const isMobile = useMediaQuery('(max-width:900px)');

    const [helpAnchorEl, setHelpAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [panAnchorEl, setPanAnchorEl] = useState<HTMLElement | null>(null);

    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const appId = useCurrentAppId()
    const appData = useCurrentApp()
    const sector = useSector(appData?.configuration?.sectorId)

    return (
        <>
            {/* Grid Background - only in edit mode */}
            {gridStyle && <Box sx={gridStyle}/>}

            <Box
                sx={{
                    position: 'absolute',
                    bottom: {
                        xs: 'max(3px, calc(3px + env(safe-area-inset-bottom)))',
                        sm: 'max(6px, calc(6px + env(safe-area-inset-bottom)))'
                    },
                    left: {
                        xs: 'max(3px, calc(3px + env(safe-area-inset-left)))',
                        sm: 'max(6px, calc(6px + env(safe-area-inset-left)))'
                    },
                    right: {
                        xs: 'max(3px, calc(3px + env(safe-area-inset-right)))',
                        sm: 'max(6px, calc(6px + env(safe-area-inset-right)))'
                    },
                    zIndex: 100,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-end',
                    pointerEvents: 'none',
                }}
            >
                {/* Left side - Help button (edit mode only) */}

                {/* Left side - Help & settings */}
                <Paper variant="outlined"
                       sx={{
                           ...flatMenuGroupSx,
                           display: 'flex',
                           flexDirection: {xs: 'column', sm: 'row'},
                           alignItems: 'center',
                       }}>
                    <Tooltip title={t('Help')} arrow disableInteractive>
                        <IconButton
                            onClick={(e) => setHelpAnchorEl(helpAnchorEl ? null : e.currentTarget)}
                            sx={{color: helpAnchorEl ? 'primary.main' : 'text.secondary'}}
                        >
                            <HelpOutline fontSize="small"/>
                        </IconButton>
                    </Tooltip>

                    {!isEditMode && (
                        <Tooltip title={t('Edit floorplan')} arrow disableInteractive>
                            <IconButton
                                size="medium"
                                onClick={() => {
                                    const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
                                    navigate(
                                        `/restaurant/floorplan/${appId}/${sector!.id!}/${restaurantId}/edit?returnTo=${encodeURIComponent(returnTo)}`,
                                    )
                                }}
                                sx={{color: 'text.secondary'}}
                                disabled={readOnlyUser}
                            >
                                <SettingsIcon fontSize="small"/>
                            </IconButton>
                        </Tooltip>
                    )}

                    {isEditMode && (
                        <Tooltip title={t('Back to floorplan')} arrow disableInteractive>
                            <IconButton
                                size="medium"
                                onClick={() => {
                                    const returnTo = searchParams.get('returnTo');
                                    navigate(returnTo || `/restaurant/${appId}`);
                                }}
                                sx={{color: 'text.secondary'}}
                            >
                                <RestaurantIcon fontSize="small"/>
                            </IconButton>
                        </Tooltip>
                    )}

                </Paper>

                {/* Help popover (shared by desktop and mobile) */}
                <Popover
                    open={Boolean(helpAnchorEl)}
                    anchorEl={helpAnchorEl}
                    onClose={() => setHelpAnchorEl(null)}
                    anchorOrigin={{vertical: 'top', horizontal: 'left'}}
                    transformOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left'
                    }}
                >
                    <Box sx={{p: 1.5, minWidth: 200}}>
                        {!isMobile && <>
                            <Typography variant="body2" color="text.disabled"
                                        fontWeight={600} sx={{mb: 0.5}}>
                                {t('Snapping')}
                            </Typography>
                            <Stack spacing={0.5}>
                                <Stack direction="row"
                                       justifyContent="space-between"
                                       alignItems="center" spacing={3}>
                                    <Typography color="text.secondary"
                                                sx={{minWidth: 80}}>{t('Free move')}</Typography>
                                    <Stack direction="row" spacing={0.5}
                                           alignItems="center">
                                        <Kbd>Shift</Kbd>
                                        <Typography component="span"
                                                    color="text.disabled"
                                                    sx={{mx: 0.5}}>{t('or')}</Typography>
                                        <Kbd>{IS_MAC ? '⌘' : 'Ctrl'}</Kbd>
                                    </Stack>
                                </Stack>
                            </Stack>

                            <Divider sx={{my: 1.5}}/>
                        </>}

                        <Typography variant="body2" color="text.disabled"
                                    fontWeight={600} sx={{mb: 0.5}}>
                            {t('Zoom')}
                        </Typography>
                        <Stack spacing={0.5}>
                            {!isMobile && <>
                                <Stack direction="row"
                                       justifyContent="space-between"
                                       alignItems="center" spacing={3}>
                                    <Typography color="text.secondary"
                                                sx={{minWidth: 80}}>{t('Zoom in')}</Typography>
                                    <Stack direction="row" spacing={0.5}
                                           alignItems="center">
                                        <Kbd>+</Kbd>
                                    </Stack>
                                </Stack>
                                <Stack direction="row"
                                       justifyContent="space-between"
                                       alignItems="center" spacing={3}>
                                    <Typography color="text.secondary"
                                                sx={{minWidth: 80}}>{t('Zoom out')}</Typography>
                                    <Stack direction="row" spacing={0.5}
                                           alignItems="center">
                                        <Kbd>−</Kbd>
                                    </Stack>
                                </Stack>
                            </>}
                            {isMobile && (
                                <Stack direction="row"
                                       justifyContent="space-between"
                                       alignItems="center" spacing={3}>
                                    <Typography color="text.secondary"
                                                sx={{minWidth: 80}}>{t('Pinch')}</Typography>
                                    <Stack direction="row" spacing={0.5}
                                           alignItems="center">
                                        <Kbd>{t('2 Fingers')}</Kbd>
                                    </Stack>
                                </Stack>
                            )}
                        </Stack>

                        {!isMobile && <>
                            <Divider sx={{my: 1.5}}/>

                            <Typography variant="body2" color="text.disabled"
                                        fontWeight={600} sx={{mb: 0.5}}>
                                {t('Pan')}
                            </Typography>
                            <Stack spacing={0.5}>
                                <Stack direction="row"
                                       justifyContent="space-between"
                                       alignItems="center" spacing={3}>
                                    <Typography color="text.secondary"
                                                sx={{minWidth: 80}}>{t('Drag')}</Typography>
                                    <Stack direction="row" spacing={0.5}
                                           alignItems="center">
                                        <Kbd>Space</Kbd>
                                    </Stack>
                                </Stack>
                                <Stack direction="row"
                                       justifyContent="space-between"
                                       alignItems="center" spacing={3}>
                                    <Typography color="text.secondary"
                                                sx={{minWidth: 80}}>{t('Scroll')}</Typography>
                                    <Stack direction="row" spacing={0.5}
                                           alignItems="center">
                                        <Kbd>{t('Middle Click')}</Kbd>
                                    </Stack>
                                </Stack>
                                <Stack direction="row"
                                       justifyContent="space-between"
                                       alignItems="center" spacing={3}>
                                    <Typography color="text.secondary"
                                                sx={{minWidth: 80}}>{t('Move viewport')}</Typography>
                                    <Stack direction="row" spacing={0.5}
                                           alignItems="center">
                                        <Kbd>↑</Kbd><Kbd>↓</Kbd><Kbd>←</Kbd><Kbd>→</Kbd>
                                    </Stack>
                                </Stack>
                            </Stack>
                        </>}

                        {isEditMode && !isMobile && <>

                            <Divider sx={{my: 1.5}}/>

                            <Typography variant="body2" color="text.disabled"
                                        fontWeight={600} sx={{mb: 0.5}}>
                                {t('Elements')}
                            </Typography>
                            <Stack spacing={0.5}>
                                <Stack direction="row"
                                       justifyContent="space-between"
                                       alignItems="center" spacing={3}>
                                    <Typography color="text.secondary"
                                                sx={{minWidth: 80}}>{t('Undo')}</Typography>
                                    <Stack direction="row" spacing={0.5}
                                           alignItems="center">
                                        <Kbd>{IS_MAC ? '⌘' : 'Ctrl'}</Kbd><span style={{paddingLeft: "0.5px", marginBottom: "-2.5px"}}>+</span><Kbd>Z</Kbd>
                                    </Stack>
                                </Stack>
                                <Stack direction="row"
                                       justifyContent="space-between"
                                       alignItems="center" spacing={3}>
                                    <Typography color="text.secondary"
                                                sx={{minWidth: 80}}>{t('Redo')}</Typography>
                                    <Stack direction="row" spacing={0.5}
                                           alignItems="center">
                                        <Kbd>{IS_MAC ? '⌘' : 'Ctrl'}</Kbd><span style={{paddingLeft: "0.5px", marginBottom: "-2.5px"}}>+</span><Kbd>Shift</Kbd><span style={{paddingLeft: "0.5px", marginBottom: "-2.5px"}}>+</span><Kbd>Z</Kbd>
                                    </Stack>
                                </Stack>
                                <Stack direction="row"
                                       justifyContent="space-between"
                                       alignItems="center" spacing={3}>
                                    <Typography color="text.secondary"
                                                sx={{minWidth: 80}}>{t('Duplicate')}</Typography>
                                    <Stack direction="row" spacing={0.5}
                                           alignItems="center">
                                        <Kbd>{IS_MAC ? '⌘' : 'Ctrl'}</Kbd><span style={{paddingLeft: "0.5px", marginBottom: "-2.5px"}}>+</span><Kbd>D</Kbd>
                                    </Stack>
                                </Stack>
                                <Stack direction="row"
                                       justifyContent="space-between"
                                       alignItems="center" spacing={3}>
                                    <Typography color="text.secondary"
                                                sx={{minWidth: 80}}>{t('Duplicate on drag')}</Typography>
                                    <Stack direction="row" spacing={0.5}
                                           alignItems="center">
                                        <Kbd>{IS_MAC ? 'Option' : 'Alt'}</Kbd>
                                        <span style={{paddingLeft: "0.5px", marginBottom: "-2.5px"}}>+</span>
                                        <Kbd>{t('Drag')}</Kbd>
                                    </Stack>
                                </Stack>
                                <Stack direction="row"
                                       justifyContent="space-between"
                                       alignItems="center" spacing={3}>
                                    <Typography color="text.secondary"
                                                sx={{minWidth: 80}}>{t('Delete')}</Typography>
                                    <Stack direction="row" spacing={0.5}
                                           alignItems="center">
                                        <Kbd>Delete</Kbd>
                                        <Typography component="span"
                                                    color="text.disabled"
                                                    sx={{mx: 0.5}}>{t('or')}</Typography>
                                        <Kbd>Backspace</Kbd>
                                    </Stack>
                                </Stack>
                                <Stack direction="row"
                                       justifyContent="space-between"
                                       alignItems="center" spacing={3}>
                                    <Tooltip title={t('To move a selection with the keyboard')} arrow disableInteractive>
                                        <Typography variant="subtitle2" sx={{
                                            mb: 0.5,
                                            textDecoration: 'underline',
                                            textDecorationStyle: 'dashed',
                                            textUnderlineOffset: '3px',
                                            cursor: 'help',
                                            width: 'fit-content',
                                            minWidth: 80
                                        }}>{t('Move selected')}</Typography>
                                    </Tooltip>

                                    <Stack direction="row" spacing={0.5}
                                           alignItems="center">
                                        <Kbd>Shift</Kbd><span style={{paddingLeft: "0.5px", marginBottom: "-2.5px"}}>+</span><Kbd>↑</Kbd><Kbd>↓</Kbd><Kbd>←</Kbd><Kbd>→</Kbd>
                                    </Stack>
                                </Stack>
                            </Stack>
                        </>}

                    </Box>
                </Popover>

                {/* Right side - Zoom controls */}
                <Paper variant="outlined" sx={{
                    ...flatMenuGroupSx,
                    display: 'flex',
                    alignItems: 'center',
                }}>
                    <Stack direction="row" sx={{alignItems: 'center'}}>
                        {/* Undo/Redo - edit mode only */}
                        {isEditMode && onUndo && onRedo && (
                            <>
                                <Tooltip title={t('Undo')} arrow disableInteractive>
                                    <span>
                                        <IconButton onClick={onUndo} disabled={!canUndo}
                                                    sx={{color: 'text.secondary'}}>
                                            <Undo fontSize="small"/>
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title={t('Redo')} arrow disableInteractive>
                                    <span>
                                        <IconButton onClick={onRedo} disabled={!canRedo}
                                                    sx={{color: 'text.secondary'}}>
                                            <Redo fontSize="small"/>
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Divider orientation="vertical" flexItem sx={{mx: 0.5}}/>
                            </>
                        )}

                        {/* Pan controls */}
                        <Tooltip title={t('Pan controls')} arrow disableInteractive>
                            <IconButton
                                onClick={(e) => setPanAnchorEl(panAnchorEl ? null : e.currentTarget)}
                                sx={{
                                    color: panAnchorEl ? 'primary.main' : 'text.secondary',
                                }}
                            >
                                <OpenWith fontSize="small"/>
                            </IconButton>
                        </Tooltip>

                        <Popover
                            open={Boolean(panAnchorEl)}
                            anchorEl={panAnchorEl}
                            anchorOrigin={{vertical: 'top', horizontal: 'center'}}
                            transformOrigin={{
                                vertical: 'bottom',
                                horizontal: 'center'
                            }}
                            onClose={() => setPanAnchorEl(null)}
                        >
                            <Box sx={{p: 1}}>
                                <Stack alignItems="center" spacing={0.25}>
                                    <IconButton size="small"
                                                onClick={() => pan(0, PAN_STEP)}
                                                sx={{p: 0.5}}>
                                        <KeyboardArrowUp fontSize="small"/>
                                    </IconButton>
                                    <Stack direction="row" spacing={0.25}>
                                        <IconButton size="small"
                                                    onClick={() => pan(PAN_STEP, 0)}
                                                    sx={{p: 0.5}}>
                                            <KeyboardArrowLeft fontSize="small"/>
                                        </IconButton>
                                        <IconButton size="small"
                                                    onClick={() => pan(-PAN_STEP, 0)}
                                                    sx={{p: 0.5}}>
                                            <KeyboardArrowRight fontSize="small"/>
                                        </IconButton>
                                    </Stack>
                                    <IconButton size="small"
                                                onClick={() => pan(0, -PAN_STEP)}
                                                sx={{p: 0.5}}>
                                        <KeyboardArrowDown fontSize="small"/>
                                    </IconButton>
                                </Stack>
                            </Box>
                        </Popover>

                        <Divider orientation="vertical" flexItem sx={{mx: 0.5}}/>

                        {/* Zoom controls */}
                        <Tooltip title={<>{t('Zoom out')} <Kbd>−</Kbd></>} arrow disableInteractive>
                            <span>
                                <IconButton onClick={zoomOut}
                                            disabled={zoom <= ZOOM_MIN}>
                                    <ZoomOut fontSize="small"/>
                                </IconButton>
                            </span>
                        </Tooltip>

                        <Tooltip title={t('Click to fit')} arrow placement="bottom">
                            <Typography
                                onClick={fitToView}
                                sx={{
                                    minWidth: 44,
                                    textAlign: 'center',
                                    fontSize: '0.8125rem',
                                    fontWeight: 600,
                                    fontFamily: 'monospace',
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    '&:hover': {color: 'primary.main'},
                                }}
                            >
                                {Math.round(zoom)}%
                            </Typography>
                        </Tooltip>

                        <Tooltip title={<>{t('Zoom in')} <Kbd>+</Kbd></>} arrow disableInteractive>
                            <span>
                                <IconButton onClick={zoomIn}
                                            disabled={zoom >= ZOOM_MAX}>
                                    <ZoomIn fontSize="small"/>
                                </IconButton>
                            </span>
                        </Tooltip>

                        {onHideReservations && !isMobile && (
                            <>
                                <Divider orientation="vertical" flexItem sx={{mx: 0.5}}/>
                                <Tooltip title={t("Hide Reservations")} arrow disableInteractive>
                                    <IconButton
                                        aria-label={t("Hide Reservations")}
                                        onClick={onHideReservations}
                                    >
                                        <MenuOpenIcon
                                            fontSize="small"
                                            sx={{transform: "scaleX(-1)"}}
                                        />
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}

                        {/* Mobile only: drawer toggle */}
                        {onOpenDrawer && (
                            <>
                                <Divider orientation="vertical" flexItem
                                         sx={{mx: 0.5}}/>
                                <Tooltip title={t('Open sidebar')} arrow disableInteractive>
                                    <IconButton
                                        onClick={onOpenDrawer}
                                        sx={{color: 'text.secondary'}}
                                    >
                                        <MenuIcon fontSize="small"/>
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}
                    </Stack>
                </Paper>
            </Box>
        </>
    );
};

export default BottomMenu;
